import { useCallback, useEffect, useRef, useState } from "react";
import { Ban, Loader2, Mail, RefreshCw, Search, ShieldCheck } from "lucide-react";
import { emailApi, isAbort, type Person, type PersonHistory } from "./api";
import { Eyebrow, Notice, Panel, Skeleton, btnGhost, btnPrimary, fmtDate, inputCls } from "./shared";

export default function PeoplePanel({ initialEmail, onPrepare }: { initialEmail?: string; onPrepare: (email: string) => void }) {
  const [q, setQ] = useState(initialEmail || "");
  const [people, setPeople] = useState<Person[] | null>(null);
  const [searchErr, setSearchErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(initialEmail || null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchCtrl = useRef<AbortController | null>(null);

  const search = useCallback(async (text: string) => {
    searchCtrl.current?.abort();
    const c = new AbortController(); searchCtrl.current = c;
    try {
      const r = await emailApi<{ people: Person[] }>(`/people?q=${encodeURIComponent(text)}`, { signal: c.signal });
      if (!c.signal.aborted) { setPeople(r.people); setSearchErr(null); }
    } catch (e) { if (!isAbort(e)) setSearchErr(e instanceof Error ? e.message : "Search failed."); }
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void search(q.trim()), 250);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q, search]);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)]">
      <div className={`space-y-3 ${selected ? "hidden lg:block" : ""}`}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input data-testid="input-people-search" className={`${inputCls} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" />
        </div>
        {searchErr && <Notice tone="error">{searchErr}</Notice>}
        {people === null ? <Skeleton rows={5} /> : people.length === 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground py-4 text-center">No matching people.</p>
            {q.includes("@") && <button data-testid="button-open-typed-email" className={btnGhost} onClick={() => setSelected(q.trim().toLowerCase())}>Look up {q.trim()}</button>}
          </div>
        ) : (
          <ul className="space-y-1">
            {people.map((p) => (
              <li key={p.email}>
                <button data-testid={`row-person-${p.email}`} onClick={() => setSelected(p.email)} className={`w-full text-left rounded-lg border px-3 py-2.5 ${selected === p.email ? "border-[#FF5C00] bg-[#FF5C00]/[0.05]" : "border-border bg-card hover:border-[#FF5C00]/40"}`}>
                  <div className="flex items-center justify-between gap-2"><p className="font-medium truncate">{p.name || p.email}</p>{p.blocked && <span className="text-[11px] text-red-700 dark:text-red-300 flex items-center gap-1"><Ban className="w-3 h-3" />Blocked</span>}</div>
                  {p.name && <p className="text-xs text-muted-foreground break-all">{p.email}</p>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Panel className={`p-5 ${selected ? "" : "hidden lg:block"}`}>
        {selected ? (
          <>
            <button data-testid="button-back-people" onClick={() => setSelected(null)} className="lg:hidden mb-4 text-sm text-muted-foreground">Back to search</button>
            <PersonDetail email={selected} onPrepare={onPrepare} onChanged={() => void search(q.trim())} />
          </>
        ) : <p className="py-20 text-center text-sm text-muted-foreground">Choose a person to see what we have sent them and what they sent back.</p>}
      </Panel>
    </div>
  );
}

function PersonDetail({ email, onPrepare, onChanged }: { email: string; onPrepare: (email: string) => void; onChanged: () => void }) {
  const [h, setH] = useState<PersonHistory | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<string | number | null>(null);
  const [prefOpen, setPrefOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [prefErr, setPrefErr] = useState<string | null>(null);
  const ctrl = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    ctrl.current?.abort();
    const c = new AbortController(); ctrl.current = c;
    setErr(null);
    try {
      const d = await emailApi<PersonHistory>(`/history?email=${encodeURIComponent(email)}`, { signal: c.signal });
      if (!c.signal.aborted) setH(d);
    } catch (e) { if (!isAbort(e)) setErr(e instanceof Error ? e.message : "Could not load history."); }
  }, [email]);
  useEffect(() => { setH(null); setPrefOpen(false); setReason(""); void load(); return () => ctrl.current?.abort(); }, [load]);

  if (err) return <div className="space-y-3"><Notice tone="error">{err}</Notice><button data-testid="button-retry-history" className={btnGhost} onClick={load}><RefreshCw className="w-4 h-4" />Retry</button></div>;
  if (!h) return <Skeleton rows={5} />;

  const blocked = !!h.preference?.blocked;
  const savePref = async () => {
    if (reason.trim().length < 3) { setPrefErr("Please give a reason."); return; }
    setBusy(true); setPrefErr(null);
    try {
      await emailApi("/preferences", { method: "PUT", body: JSON.stringify({ email: h.email, blocked: !blocked, reason: reason.trim() }) });
      setPrefOpen(false); setReason(""); await load(); onChanged();
    } catch (e) { setPrefErr(e instanceof Error ? e.message : "Could not update."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid={`detail-person-${h.email}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0"><Eyebrow>Person</Eyebrow><h3 className="mt-1 font-serif text-2xl break-all">{h.email}</h3>
          <p className="mt-1 text-sm">{h.eligibility ? <span className="text-red-700 dark:text-red-300">Not eligible for marketing: {h.eligibility}</span> : <span className="text-emerald-700 dark:text-emerald-300">Eligible for marketing email</span>}</p>
          {blocked && <p className="text-xs text-muted-foreground mt-0.5">Manual block{h.preference?.reason ? `: ${h.preference.reason}` : ""}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="button-prepare-email" onClick={() => onPrepare(h.email)} className={btnPrimary}><Mail className="w-4 h-4" />Prepare email</button>
          <button data-testid="button-toggle-block" onClick={() => setPrefOpen((v) => !v)} className={btnGhost}>{blocked ? <><ShieldCheck className="w-4 h-4" />Remove block</> : <><Ban className="w-4 h-4" />Block</>}</button>
        </div>
      </div>

      {prefOpen && (
        <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-3">
          {blocked
            ? <Notice tone="warn">Removing this block only lifts the manual block set here. It never overrides earlier unsubscribes or delivery suppressions after bounces or complaints.</Notice>
            : <Notice>Blocking stops all marketing email from this workspace. Essential account messages may still be sent.</Notice>}
          <input data-testid="input-pref-reason" className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (recorded in the audit trail)" />
          {prefErr && <Notice tone="error">{prefErr}</Notice>}
          <button data-testid="button-save-preference" disabled={busy} onClick={savePref} className={btnPrimary}>{busy && <Loader2 className="w-4 h-4 animate-spin" />}{blocked ? "Remove manual block" : "Block marketing email"}</button>
        </div>
      )}

      <div>
        <Eyebrow>Email activity · {h.history.length}</Eyebrow>
        <p className="mt-1 text-xs text-muted-foreground">Includes older emails only where existing logs recorded them. This may not be a complete history.</p>
        {h.history.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No recorded emails for this address.</p> : (
          <ul className="mt-2 divide-y divide-border rounded-lg border border-border">
            {h.history.map((x) => (
              <li key={x.id} className="px-3 py-2.5">
                <button data-testid={`row-history-${x.id}`} className="w-full text-left" onClick={() => setOpen(open === x.id ? null : x.id)}>
                  <div className="flex items-start justify-between gap-2"><p className="font-medium text-sm">{x.subject}</p><span className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtDate(x.at, false)}</span></div>
                  <p className="text-xs text-muted-foreground">{x.source} · {x.status}{x.event ? ` · ${x.event}` : ""}</p>
                </button>
                {open === x.id && x.body_text && <pre className="mt-2 whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-xs font-sans leading-relaxed">{x.body_text}</pre>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <Eyebrow>Replies received · {h.replies.length}</Eyebrow>
        {h.replies.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No recorded replies.</p> : (
          <ul className="mt-2 space-y-2">
            {h.replies.map((r) => <li key={r.id} className="rounded-lg border border-border p-3"><p className="text-sm font-medium">{r.subject}</p><p className="text-[11px] text-muted-foreground">{fmtDate(r.received_at)}</p>{r.body_text && <p className="mt-1 text-sm whitespace-pre-wrap line-clamp-6">{r.body_text}</p>}</li>)}
          </ul>
        )}
      </div>
      {h.events.length > 0 && (
        <div><Eyebrow>Preference events</Eyebrow>
          <ul className="mt-2 space-y-1 text-sm">{h.events.map((e, i) => <li key={i}>{e.action}{e.actor ? ` by ${e.actor}` : ""} <span className="text-xs text-muted-foreground">{fmtDate(e.created_at)}</span></li>)}</ul>
        </div>
      )}
    </div>
  );
}
